<template>
  <b-modal v-model="visible" size="lg" centered hide-header hide-footer
           no-close-on-backdrop body-class="p-0" content-class="mam-content">
    <!-- Hero -->
    <div class="mam__hero">
      <div class="mam__hero-icon"><i class="fa-solid fa-sliders"></i></div>
      <div class="mam__hero-text">
        <h4 class="mb-0">
          {{ isBulk ? $t('finance.actionModal.bulkTitle', { count: targets.length }) : $t('finance.actionModal.title') }}
        </h4>
        <small v-if="!isBulk && target">
          #{{ target.id }} · {{ target.clientDetails && target.clientDetails.email }} ·
          <strong>{{ convertToEURO(target.amount) }}</strong>
        </small>
        <small v-else>{{ targets.length }} {{ $t('finance.actionModal.selected') }}</small>
      </div>
      <button type="button" class="mam__close" @click="close" :disabled="running">
        <feather-icon icon="XIcon" size="18"/>
      </button>
    </div>

    <div class="mam__body">
      <!-- Action options (first-level monetary actions, permission-gated) -->
      <div class="mam__label">{{ $t('finance.actionModal.pickAction') }}</div>
      <div class="mam__options">
        <button v-for="opt in options" :key="opt.key" type="button" class="mam-opt"
                :class="[{ 'is-active': action === opt.key }, `mam-opt--${opt.tone}`]"
                :disabled="running" @click="action = opt.key">
          <span class="mam-opt__icon"><i :class="opt.icon"></i></span>
          <span class="mam-opt__body">
            <span class="mam-opt__title">{{ opt.title }}</span>
            <span class="mam-opt__hint">{{ opt.hint }}</span>
          </span>
        </button>
      </div>

      <!-- Comment action: internal note + client-visible comment, same pair
           as the per-row comment modal -->
      <template v-if="action === 'comment'">
        <div class="mam__label mt-1">
          <i class="fa-solid fa-user-shield mr-25"></i>{{ $t('monetary.commentModal.internalTitle') }}
        </div>
        <b-form-textarea v-model="commentInternal" rows="2" :disabled="running"
                         :placeholder="$t('monetary.commentModal.internalHint')"/>
        <div class="mam__label mt-1">
          <i class="fa-solid fa-eye mr-25"></i>{{ $t('monetary.commentModal.clientTitle') }}
        </div>
        <b-form-textarea v-model="commentClient" rows="2" :disabled="running"
                         :placeholder="$t('monetary.commentModal.clientHint')"/>
        <small v-if="!canExecute" class="text-danger">
          {{ $t('finance.actionModal.commentRequired') }}
        </small>
      </template>

      <!-- Delete: make the danger explicit -->
      <b-alert v-else-if="action === 'delete'" show variant="danger" class="mt-1 mb-0 p-75">
        <i class="fa-solid fa-triangle-exclamation mr-25"></i>{{ $t('monetary.actionModal.deleteWarning') }}
      </b-alert>

      <!-- Progress -->
      <div v-if="running || progress.total > 0" class="mam__progress mt-1">
        <b-progress :value="progress.done" :max="progress.total" height="8px"
                    :variant="progress.fail ? 'danger' : 'success'"/>
        <small class="text-muted">
          <template v-if="running">{{ $t('finance.actionModal.running', { done: progress.done, total: progress.total }) }}</template>
          <template v-else>
            {{ $t('finance.actionModal.resultOk', { count: progress.ok }) }}<template v-if="progress.fail"> · <span class="text-danger">{{ $t('finance.actionModal.resultFail', { count: progress.fail }) }}</span></template>
          </template>
        </small>
      </div>

      <!-- Footer -->
      <div class="mam__footer">
        <b-button variant="outline-secondary" :disabled="running" @click="close">
          {{ $t('operations.cancel') }}
        </b-button>
        <b-button :variant="executeVariant" :disabled="running || !canExecute" @click="run">
          <b-spinner v-if="running" small class="mr-25"/>
          <i v-else :class="activeOption.icon" class="mr-25"></i>
          {{ $t('finance.actionModal.execute') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'

/**
 * Bulk/single FIRST-LEVEL monetary actions (the finance dashboard's modal
 * covers the second-level finance flow): comment, approve, decline, fake
 * approve and delete — executed sequentially per row with a progress bar.
 */
export default {
  name: 'MonetaryActionModal',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  data: () => ({
    visible: false,
    targets: [],
    action: 'approve',
    commentInternal: '',
    commentClient: '',
    running: false,
    progress: { done: 0, total: 0, ok: 0, fail: 0 },
  }),
  computed: {
    isBulk() {
      return this.targets.length > 1
    },
    target() {
      return this.targets.length === 1 ? this.targets[0] : null
    },
    options() {
      const opts = [
        { key: 'comment', tone: 'info', icon: 'fa-solid fa-comment-dots', title: this.$t('finance.comment'), hint: this.$t('monetary.actionModal.commentHint'), allowed: true },
        { key: 'approve', tone: 'success', icon: 'fa-solid fa-circle-check', title: this.$t('monetary.bulkApprove'), hint: this.$t('monetary.actionModal.approveHint'), allowed: this.hasPermission(this.permissions.MONETARY_APPROVE) },
        { key: 'decline', tone: 'danger', icon: 'fa-solid fa-circle-xmark', title: this.$t('monetary.actionModal.declineTitle'), hint: this.$t('monetary.actionModal.declineHint'), allowed: this.hasPermission(this.permissions.MONETARY_APPROVE) },
        { key: 'fake', tone: 'warning', icon: 'fa-solid fa-wand-magic-sparkles', title: this.$t('monetary.actionModal.fakeTitle'), hint: this.$t('monetary.actionModal.fakeHint'), allowed: this.hasPermission(this.permissions.MONETARY_APPROVE) },
        { key: 'delete', tone: 'danger', icon: 'fa-solid fa-trash', title: this.$t('operations.delete'), hint: this.$t('monetary.actionModal.deleteHint'), allowed: this.hasPermission(this.permissions.MONETARY_DELETE) },
      ]
      return opts.filter(o => o.allowed)
    },
    activeOption() {
      return this.options.find(o => o.key === this.action) || this.options[0]
    },
    executeVariant() {
      const map = { info: 'info', success: 'success', danger: 'danger', warning: 'warning' }
      return map[this.activeOption.tone] || 'primary'
    },
    canExecute() {
      if (!this.targets.length) return false
      if (this.action === 'comment') {
        return this.commentInternal.trim().length > 0 || this.commentClient.trim().length > 0
      }
      return true
    },
  },
  methods: {
    ...mapActions('monetary', ['handleEvent', 'updateMonetary']),
    ...mapActions('monetary', { deleteMonetary: 'delete' }),
    /** @param {Array<Object>} items transaction row objects (1 = single mode) */
    open(items) {
      this.targets = Array.isArray(items) ? items.filter(Boolean) : [items].filter(Boolean)
      this.action = this.options.length ? this.options[0].key : 'comment'
      this.commentInternal = ''
      this.commentClient = ''
      this.running = false
      this.progress = { done: 0, total: 0, ok: 0, fail: 0 }
      this.visible = true
    },
    close() {
      if (this.running) return
      this.visible = false
    },
    async run() {
      if (!this.canExecute || this.running) return
      this.running = true
      this.progress = { done: 0, total: this.targets.length, ok: 0, fail: 0 }
      for (const tx of this.targets) {
        try {
          if (this.action === 'comment') {
            // Same write as the per-row comment modal: canonical `comment`,
            // legacy paymentDetails.comments kept in sync, clientComment for
            // the webtrader.
            const monetary = { ...tx, paymentDetails: { ...(tx.paymentDetails || {}) } }
            if (this.commentInternal.trim()) {
              monetary.comment = this.commentInternal
              monetary.paymentDetails.comments = this.commentInternal
            }
            if (this.commentClient.trim()) {
              monetary.clientComment = this.commentClient
            }
            await this.updateMonetary({ monetary, id: tx.id })
          } else if (this.action === 'delete') {
            await this.deleteMonetary({ id: tx.id })
          } else {
            // approve | decline | fake — the backend stamps who did it (statusUserId).
            await this.handleEvent({ id: tx.id, event: this.action })
          }
          this.progress.ok++
        } catch (err) {
          this.progress.fail++
        }
        this.progress.done++
      }
      this.running = false
      if (this.progress.fail === 0) {
        this.showSuccessNotification(this.$t('finance.actionModal.resultOk', { count: this.progress.ok }))
        this.visible = false
      } else {
        this.showWarningNotification(
            `${this.$t('finance.actionModal.resultOk', { count: this.progress.ok })} · ${this.$t('finance.actionModal.resultFail', { count: this.progress.fail })}`)
      }
      this.$emit('done')
    },
  },
}
</script>

<style lang="scss">
/* Same visual language as the finance action modal (fam-*), own prefix so
   this chunk is self-contained wherever it loads. */
.mam-content {
  border: none;
  border-radius: .8rem;
  overflow: hidden;
}
.mam__hero {
  display: flex;
  align-items: center;
  gap: .9rem;
  padding: 1.1rem 1.4rem;
  color: #fff;
  background: linear-gradient(120deg, #1f2a44, #3b5b92);
}
.mam__hero-icon {
  width: 44px;
  height: 44px;
  border-radius: .65rem;
  background: rgba(255, 255, 255, .18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.mam__hero-text { flex: 1; min-width: 0; h4 { color: #fff; font-weight: 700; } small { opacity: .9; } }
.mam__close {
  border: none;
  background: rgba(255, 255, 255, .15);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s ease, transform .2s ease;
  &:hover { background: rgba(255, 255, 255, .3); transform: rotate(90deg); }
}

.mam__body { padding: 1.15rem 1.4rem 1.3rem; }
.mam__label {
  font-size: .74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .4px;
  color: #a6a4b0;
  margin-bottom: .5rem;
}

.mam__options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: .55rem;
}
.mam-opt {
  --mam-c: #7367f0;
  display: flex;
  align-items: center;
  gap: .6rem;
  border: 1px solid #d8d6de;
  border-radius: .65rem;
  background: transparent;
  padding: .6rem .7rem;
  cursor: pointer;
  text-align: left;
  transition: all .15s ease;

  &--info { --mam-c: #00cfe8; .mam-opt__icon { background: rgba(0, 207, 232, .14); } }
  &--success { --mam-c: #28c76f; .mam-opt__icon { background: rgba(40, 199, 111, .14); } }
  &--danger { --mam-c: #ea5455; .mam-opt__icon { background: rgba(234, 84, 85, .14); } }
  &--warning { --mam-c: #ff9f43; .mam-opt__icon { background: rgba(255, 159, 67, .14); } }

  &:hover:not(:disabled) { border-color: var(--mam-c); }
  &.is-active {
    border-color: var(--mam-c);
    box-shadow: inset 0 0 0 1px var(--mam-c);
    background: rgba(0, 0, 0, .015);
  }
  &__icon {
    width: 34px;
    height: 34px;
    border-radius: .55rem;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--mam-c);
    font-size: .9rem;
  }
  &__body { min-width: 0; display: flex; flex-direction: column; line-height: 1.25; }
  &__title { font-size: .84rem; font-weight: 700; color: #5e5873; }
  &__hint { font-size: .7rem; color: #b9b9c3; }
}

.mam__progress { display: flex; flex-direction: column; gap: .25rem; }
.mam__footer {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  margin-top: 1.1rem;
}

.dark-layout {
  .mam-content { background: #283046; color: #d0d2d6; }
  .mam-opt {
    border-color: #3b4253;
    &.is-active { background: rgba(255, 255, 255, .03); }
    &__title { color: #d0d2d6; }
  }
}
</style>
